import { Eye, Flame, ImageIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import type { CommunityPost } from "@/types/mission-community"

/**
 * OKS-392 — A single community-feed tile (W11). The WHOLE tile is one tap
 * target: an `<a>` opening the post on TikTok in a new tab. Intra-brand social
 * proof, so creator name/avatar ARE shown.
 *
 * Anatomy (per UX Spec §2):
 *   - background: TikTok thumbnail (`object-cover`, lazy) → branded fallback
 *     when `thumbnail_url` is null;
 *   - bottom gradient scrim so overlays stay legible on bright thumbnails;
 *   - bottom-left: avatar (image → initials fallback) + handle/name;
 *   - bottom-right: view-count overlay — OMITTED when `view_count` is null
 *     (V1 always null; forward-compat seam);
 *   - top-left: 🔥 Featured chip — rendered only when `featured` is true
 *     (V1 always false; the rule is conditional on view data).
 *
 * Accessibility: the link carries a full accessible name
 * ("Post by @handle — open on TikTok"); Featured + view count expose textual
 * labels (not color/icon alone).
 */
export interface CommunityTileProps {
  post: CommunityPost
}

export function CommunityTile({ post }: CommunityTileProps) {
  const { t } = useTranslation()
  const initials = useInitials()

  // Prefer handle for the accessible name; fall back to the display name.
  const who = post.creator_handle
    ? `@${post.creator_handle}`
    : post.creator_name

  return (
    <a
      href={post.tiktok_video_url}
      target="_blank"
      rel="noopener noreferrer"
      data-tile
      data-testid="community-tile"
      aria-label={t("creator_mission_community.tile.open_aria", { who })}
      className={cn(
        "group relative block aspect-square overflow-hidden rounded-lg",
        "bg-muted transition active:scale-[0.98] active:opacity-90",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
      )}
    >
      {post.thumbnail_url ? (
        <img
          src={post.thumbnail_url}
          alt=""
          loading="lazy"
          decoding="async"
          data-testid="community-tile-thumb"
          className="size-full object-cover transition-opacity duration-300 group-hover:opacity-95"
        />
      ) : (
        <div
          data-testid="community-tile-thumb-fallback"
          className="text-muted-foreground flex size-full items-center justify-center"
          aria-hidden="true"
        >
          <ImageIcon className="size-8" />
        </div>
      )}

      {/* Bottom scrim for overlay legibility. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent"
      />

      {/* Featured chip — top-left. V1: never renders (featured === false). */}
      {post.featured && (
        <Badge
          data-testid="community-tile-featured"
          className="absolute top-1.5 left-1.5 gap-1 bg-orange-500 text-white"
        >
          <Flame className="size-3" aria-hidden="true" />
          {t("creator_mission_community.tile.featured")}
        </Badge>
      )}

      {/* Creator overlay — bottom-left. */}
      <div className="absolute inset-x-1.5 bottom-1.5 flex items-center gap-1.5">
        <Avatar className="size-6 shrink-0 border border-white/40">
          {post.avatar_url && (
            <AvatarImage
              src={post.avatar_url}
              alt=""
              data-testid="community-tile-avatar"
            />
          )}
          <AvatarFallback
            className="bg-black/40 text-[10px] text-white"
            data-testid="community-tile-initials"
          >
            {initials(post.creator_name)}
          </AvatarFallback>
        </Avatar>
        <span className="truncate text-xs font-medium text-white drop-shadow">
          {who}
        </span>
      </div>

      {/* View-count overlay — bottom-right. V1: never renders (view_count null). */}
      {post.view_count !== null && (
        <div
          data-testid="community-tile-views"
          className="absolute right-1.5 bottom-1.5 flex items-center gap-1 text-xs font-medium text-white drop-shadow"
        >
          <Eye className="size-3.5" aria-hidden="true" />
          <span
            aria-label={t("creator_mission_community.tile.views", {
              count: post.view_count,
            })}
          >
            {compactCount(post.view_count)}
          </span>
        </div>
      )}
    </a>
  )
}

/** Compact view-count formatter ("12.4K"). Forward-compat — unused in V1. */
function compactCount(value: number): string {
  return new Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value)
}
